import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HomeLayout } from "../layout/HomeLayout";
import { HomeHero } from "../home/HomeHero";
import { PillarCards } from "../home/PillarCards";
import { HomeClassesIndex } from "../home/HomeClassesIndex";
import { HomeIndexSection } from "../home/HomeIndexSection";
import { useBordonaDashboard } from "../../hooks/useBordonaDashboard";
import { useStudentAuth } from "../../hooks/useStudentAuth";
import { HOME_THEME as T } from "../../lib/homeTheme";

function fmtWhen(ts: number): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
  });
}

export const BordonaDashboardView: React.FC = () => {
  const navigate = useNavigate();
  const d = useBordonaDashboard();
  const { profile } = useStudentAuth();

  const displayName = profile
    ? `${profile.firstName} ${profile.lastName}`.trim()
    : d.profile?.firstName ?? "Músico";

  const earnedBadges = d.badges.filter((b) => b.earned).length;

  const progressItems = useMemo(() => {
    const items: { label: string; meta?: string; onClick?: () => void }[] = [];

    items.push({
      label: "Gimnasio de dedos",
      meta: `${d.gym.completedExercises.length} ejercicios · ${d.gymTimeLabel}`,
      onClick: () => navigate("/fingers"),
    });

    d.gymLevels.forEach((lv) => {
      items.push({
        label: lv.name,
        meta: `${lv.done}/${lv.total}${lv.avgScore > 0 ? ` · ${lv.avgScore}%` : ""}`,
        onClick: () => navigate("/fingers"),
      });
    });

    d.songActivity.slice(0, 6).forEach((s) => {
      items.push({
        label: s.songTitle,
        meta: `${s.accuracyPct}% · ${s.mode} · ${fmtWhen(s.completedAt)}`,
        onClick: () => navigate(`/songs/${s.songId}`),
      });
    });

    items.push({
      label: "Logros desbloqueados",
      meta: `${earnedBadges} de ${d.badges.length}`,
    });

    items.push({
      label: "XP total",
      meta: `${d.xp.total.toLocaleString("es-CL")} · nivel ${d.levelInfo.level}`,
    });

    items.push({
      label: "Minutos de práctica",
      meta: String(d.totalPracticeMinutes),
    });

    items.push({
      label: "Tareas completadas",
      meta: String(d.stats.tasksCompleted),
    });

    if (d.favoritesByType.scales.length > 0) {
      items.push({
        label: "Escalas guardadas",
        meta: String(d.favoritesByType.scales.length),
        onClick: () => navigate("/scales/explorer"),
      });
    }

    if (d.favoritesByType.chords.length > 0) {
      items.push({
        label: "Acordes guardados",
        meta: String(d.favoritesByType.chords.length),
        onClick: () => navigate("/chords/explorer"),
      });
    }

    return items;
  }, [d, earnedBadges, navigate]);

  const continueItems = useMemo(() => {
    const items: { label: string; meta?: string; onClick?: () => void }[] = [];
    if (d.lastSong) {
      items.push({
        label: d.lastSong.songTitle,
        meta: `${d.lastSong.accuracyPct}% · ${d.lastSong.mode}`,
        onClick: () => navigate(`/songs/${d.lastSong!.songId}`),
      });
    }
    return items;
  }, [d.lastSong, navigate]);

  return (
    <HomeLayout>
      <div className="mx-auto flex max-w-7xl flex-col gap-12 px-8 pb-20">
        <HomeHero
          compact
          greeting={`Hola,\n${displayName}`}
          tagline="Explora libremente, aprende con guía y practica tu repertorio."
          stats={[
            { value: d.streak, label: "Días de racha" },
            { value: d.levelInfo.level, label: "Nivel" },
            {
              value: d.stats.averageScore > 0 ? `${Math.round(d.stats.averageScore)}%` : "—",
              label: "Nota media",
            },
          ]}
        />

        <PillarCards />

        <HomeClassesIndex isTeacher={false} />

        {continueItems.length > 0 && (
          <HomeIndexSection title="Continuar" items={continueItems} compact />
        )}

        <HomeIndexSection title="Mi progreso" items={progressItems} compact />

        {d.loading && (
          <p className="text-left text-xs tracking-widest uppercase animate-pulse" style={{ color: T.dim }}>
            Sincronizando…
          </p>
        )}
      </div>
    </HomeLayout>
  );
};
